const TIMEZONE_DENVER = 'America/Denver';

const DayOfWeek = {

    /**
     * The singleton instance for the day-of-week of Monday.
     * This has the numeric value of {@code 1}.
     */
    MONDAY: 1,
    /**
     * The singleton instance for the day-of-week of Tuesday.
     * This has the numeric value of {@code 2}.
     */
    TUESDAY: 2,
    /**
     * The singleton instance for the day-of-week of Wednesday.
     * This has the numeric value of {@code 3}.
     */
    WEDNESDAY: 3,
    /**
     * The singleton instance for the day-of-week of Thursday.
     * This has the numeric value of {@code 4}.
     */
    THURSDAY: 4,
    /**
     * The singleton instance for the day-of-week of Friday.
     * This has the numeric value of {@code 5}.
     */
    FRIDAY: 5,
    /**
     * The singleton instance for the day-of-week of Saturday.
     * This has the numeric value of {@code 6}.
     */
    SATURDAY: 6,
    /**
     * The singleton instance for the day-of-week of Sunday.
     * This has the numeric value of {@code 7}.
     */
    SUNDAY: 7
};

const Month = {
    /**
     * The singleton instance for the month of January with 31 days.
     * This has the numeric value of {@code 1}.
     */
    JANUARY: 1,
    /**
     * The singleton instance for the month of February with 28 days, or 29 in a leap year.
     * This has the numeric value of {@code 2}.
     */
    FEBRUARY: 2,
    /**
     * The singleton instance for the month of March with 31 days.
     * This has the numeric value of {@code 3}.
     */
    MARCH: 3,
    /**
     * The singleton instance for the month of April with 30 days.
     * This has the numeric value of {@code 4}.
     */
    APRIL: 4,
    /**
     * The singleton instance for the month of May with 31 days.
     * This has the numeric value of {@code 5}.
     */
    MAY: 5,
    /**
     * The singleton instance for the month of June with 30 days.
     * This has the numeric value of {@code 6}.
     */
    JUNE: 6,
    /**
     * The singleton instance for the month of July with 31 days.
     * This has the numeric value of {@code 7}.
     */
    JULY: 7,
    /**
     * The singleton instance for the month of August with 31 days.
     * This has the numeric value of {@code 8}.
     */
    AUGUST: 8,
    /**
     * The singleton instance for the month of September with 30 days.
     * This has the numeric value of {@code 9}.
     */
    SEPTEMBER: 9,
    /**
     * The singleton instance for the month of October with 31 days.
     * This has the numeric value of {@code 10}.
     */
    OCTOBER: 10,
    /**
     * The singleton instance for the month of November with 30 days.
     * This has the numeric value of {@code 11}.
     */
    NOVEMBER: 11,
    /**
     * The singleton instance for the month of December with 31 days.
     * This has the numeric value of {@code 12}.
     */
    DECEMBER: 12
};


luxon.Settings.defaultZone = TIMEZONE_DENVER;

/**
 * If it's later than 3:45, it's tomorrow.
 * @param {luxon.DateTime|Date} today The date to adjust
 * @return {luxon.DateTime} if it's before 3:45 and tomorrow if it's after.
 * @type {luxon.DateTime}
 */
function adjustDateForTimeOFDay(today) {
    let dateToReturn, threeFortyFive;

    threeFortyFive = dateToReturn = toDenverTimeZone(today);

    threeFortyFive = threeFortyFive.set({hours: 15, minutes: 45, seconds: 0, millisecond: 0});

    if(dateToReturn > threeFortyFive) {
        return dateToReturn.plus({days: 1}).set({ hours: 0, minutes: 0, seconds: 0, millisecond: 0});
    }

    return dateToReturn;
}

/**
 * Determines the difference in days between the provided dates.
 * @param {Date} startDate Start date to calculate
 * @param {Date} endDate Optional (Default Today). End date to use to calculate difference.
 * @return {number} Number of days apart
 * @type {(startDate: Date, endDate: Date) => number}
 */
const daysApart = (startDate, endDate = new Date()) => {
    let days, diff, endClone, startClone;

    if(isUndefinedOrNull(startDate)){
        throw new ReferenceError('Start Date must be provided');
    }

    // Clone Dates to compare
    startClone = new Date();
    endClone = new Date();
    startClone.setTime(startDate.getTime());
    endClone.setTime(endDate.getTime());

    // set time to the same thing since we only care about date
    startClone.setHours(0, 0, 0, 0);
    endClone.setHours(0, 0, 0, 0);

    // Calculate Difference
    diff = startClone.getTime() - endClone.getTime();

    // Convert to Days
    // Difference -> Seconds -> Mins -> Hours -> Days
    days = (((diff/1000) / 60) / 60) / 24;

    return days;
};

/**
 * This method will convert the provided time to the proper format for display
 * @param {string} timeToFormat Time string to format
 * @returns {string} Formatted time
 * @type {(timeToFormat: string) => string}
 */
const formatTimeForDisplay = (timeToFormat) => {
    if(isEmpty(timeToFormat)){
        return timeToFormat;
    }

    return timeToFormat.replace('a', ' am')
                       .replace('p', ' pm')
                       .replace(/^0+/, '');
};

const formatTimeForNoteAttribute = (timeToFormat) => {
    if(isEmpty(timeToFormat)){
        return timeToFormat;
    }

    return timeToFormat.replace('am', ':00 AM')
                       .replace('pm', ':00 PM')
};


const formatTimeForNoteAttributeInfiniteScheduler = (timeToFormat) => {
    if(isEmpty(timeToFormat)){
        return timeToFormat;
    }

    return timeToFormat.replace('am', ' AM')
                       .replace('pm', ' PM')
};

const formatTimeForOrderEdit = (timeToFormat) => {
    if(isEmpty(timeToFormat)){
        return timeToFormat;
    }

    return timeToFormat.replace('am', 'a')
                       .replace('pm', 'p');
};

/**
 * Skip daysToSkip and Effective holidays and weekends
 * @param {luxon.DateTime|Date} today The from-date
 * @param {number} daysToSkip The number of days to skip or delay
 * @return {luxon.DateTime} the next business day
 * @type {(today: luxon.DateTime|Date, daysToSkip: number) => Date}
 */
function getNextBusinessDay(today, daysToSkip) {
    let nextBusinessDay = adjustDateForTimeOFDay(today);

    for(let i = 0; i < daysToSkip; i++) {
        if(isEffectiveHoliday(nextBusinessDay)) {
            nextBusinessDay = nextBusinessDay.plus({days: 1});
        }
        if(isSaturday(nextBusinessDay)) {
            nextBusinessDay = nextBusinessDay.plus({days: 2});
        }
        else if(isSunday(nextBusinessDay)) {
            nextBusinessDay = nextBusinessDay.plus({days: 1});
        }
        if(isEffectiveHoliday(nextBusinessDay)) {
            // It's monday
            nextBusinessDay = nextBusinessDay.plus({days: 1});
        }
        nextBusinessDay = nextBusinessDay.plus({days: 1});
    }

    if(isSaturday(nextBusinessDay)) {
        nextBusinessDay = nextBusinessDay.plus({days: 2});
    }
    if(isEffectiveHoliday(nextBusinessDay)) {
        nextBusinessDay = nextBusinessDay.plus({days: 1});
        if(isSaturday(nextBusinessDay)) {
            nextBusinessDay = nextBusinessDay.plus({days: 2});
        }
    }
    return nextBusinessDay.toJSDate();
}

/**
 * This method will return the Date/DateTime string for the provided object in either America/Denver
 * or Zulu timezone
 * @param {Date} dateObject Date object to use
 * @param {boolean} dateOnly (Optional. Default true) Whether to return the Date string value.
 * @param {boolean} mountainTimeZone (Optional. Default false) Whether to return the Date/Date Time
 *                                   string value in America/Denver timezone
 * @return {(
 *      dateObject: Date, dateOnly: boolean, mountainTimeZone: boolean
 *      | dateObject: Date, dateOnly: boolean
 *      | dateObject: Date) => string} Date/Date Time string value
 */
const getDateString = (dateObject, dateOnly, mountainTimeZone) =>{
    if(isUndefinedOrNull(dateOnly)){
        dateOnly = true;
    }
    if(isUndefinedOrNull(mountainTimeZone)){
        mountainTimeZone = false;
    }

    dateObject = toDenverTimeZone(dateObject);

    if(!mountainTimeZone){
        dateObject = dateObject.toUTC();
    }

    if(dateOnly) {
        return dateObject.toISODate();
    }

    return dateObject.startOf('second').toISO({suppressMilliseconds: true});
};

/**
 * This method will determine what date and time slot to use for OSS Install Today option
 * @param {string} calendarToUse (Optional. default '14'). Calendar to use for Install today
 *                               calculation
 * @return {(string) => [string, string, string]} Array with string values for date and timeslot
 */
const getInstallTodayAndTimeSlots = (calendarToUse = '14') => {
    let fromTimeSlot, today, todayDate, toTimeSlot;
    const formatHour = (timeSlot) => timeSlot.replace(/ ([AP])M/, ':00 $1M');
    today = new Date();
    todayDate = getDateString(today, true, true);
    switch (calendarToUse){
        case '14':
            // Set start to the next Even Hour
            if (today.getHours() % 2 === 1) {
                today.setHours(today.getHours() + 1);
            }

            fromTimeSlot = today.toLocaleString('en-US', {
                hour: 'numeric',
                hour12: true
            });

            // If local current time is past 5 pm then set appointment time to 4P - 6P
           if (today.getHours() < 17) {
                today.setHours(today.getHours() + 2);
                toTimeSlot = today.toLocaleString('en-US', {
                    hour: 'numeric',
                    hour12: true
                })
            }
            else { // Set start time 4P and end time 6P if the current local time is past 5pm
                if (today.getHours() > 16) {
                    today.setHours(16);
                }
                fromTimeSlot = today.toLocaleString('en-US', {
                    hour: 'numeric',
                    hour12: true
                });
                today.setHours(today.getHours() + 2);
                toTimeSlot = today.toLocaleString('en-US', {
                    hour: 'numeric',
                    hour12: true
                })
            }

            // *** FORMATTING OF TIME SLOT FOR INSTALL TODAY FLOW ***
            if (fromTimeSlot || toTimeSlot) {
                fromTimeSlot = formatHour(fromTimeSlot);
                toTimeSlot = formatHour(toTimeSlot);
            }
            break;
        default:
            throw new Error("Undefined Calendar provided.")
    }

    return [todayDate, fromTimeSlot, toTimeSlot];
};

/**
 * Is today Christmas?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Christmas
 * @type {boolean}
 */
function isChristmasDay(zonedDateTime) {
    // December 25th
    return zonedDateTime.day === 25
        && zonedDateTime.month === Month.DECEMBER;
}

/**
 * An effective Holiday is:
 * <P><UL>
 *     <LI>Today if today is a Holiday</LI>
 *     <LI>Today if today is the Friday before a Saturday Holiday</LI>
 *     <LI>Today if today is the Monday after a Sunday Holiday</LI>
 * </UL>
 * @param {luxon.DateTime} zonedDateTime The date in question, often today but will become tomorrow after 3:45PM
 * @return {boolean} true when today is an effective holiday
 * @type {boolean }
 */
function isEffectiveHoliday(zonedDateTime) {
    if(isHoliday(zonedDateTime)) {
        return true;
    }
    if(isFriday(zonedDateTime) && isHoliday(zonedDateTime.plus({days: 1}))) {
        return true;
    }
    return isMonday(zonedDateTime) && isHoliday(zonedDateTime.minus({days: 1}));
}

/**
 * Is given date Friday?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Friday
 * @type {boolean}
 */
function isFriday(zonedDateTime) {
    return zonedDateTime.weekday === DayOfWeek.FRIDAY;
}

/**
 * A Holiday is: New Years, Memorial Day, Independence Day, Labor Day, Thanksgiving or Christmas
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when zonedDateTime is a Holiday
 * @type {boolean}
 */
function isHoliday(zonedDateTime) {
    return isNewYearsDay(zonedDateTime) ||
        isMemorialDay(zonedDateTime) ||
        isIndependenceDay(zonedDateTime) ||
        isLaborDay(zonedDateTime) ||
        isThanksgivingDay(zonedDateTime) ||
        isChristmasDay(zonedDateTime);
}

/**
 * Is today Independence Day?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Independence Day
 * @type {boolean}
 */
function isIndependenceDay(zonedDateTime) {
    // Fourth of July
    return zonedDateTime.day === 4
        && zonedDateTime.month === Month.JULY;
}

/**
 * Is today Labor Day?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Labor Day
 * @type {boolean}
 */
function isLaborDay(zonedDateTime) {
    // First Monday in September
    return zonedDateTime.month === Month.SEPTEMBER
        && zonedDateTime.day <= 7
        && isMonday(zonedDateTime);
}

/**
 * Is today Memorial Day?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Memorial Day
 * @type {boolean}
 */
function isMemorialDay(zonedDateTime) {
    // Last Monday in May
    return zonedDateTime.month === Month.MAY
        && isMonday(zonedDateTime)
        && zonedDateTime.day >= 25;
}

/**
 * Is given date Monday?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Monday
 * @type {boolean}
 */
function isMonday(zonedDateTime) {
    return zonedDateTime.weekday === DayOfWeek.MONDAY;
}

/**
 * Is today New Years Day?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is
 * @type {boolean}
 */
function isNewYearsDay(zonedDateTime) {
    return zonedDateTime.day === 1
        && zonedDateTime.month === Month.JANUARY;
}

/**
 * Is given date Saturday?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Saturday
 * @type {boolean}
 */
function isSaturday(zonedDateTime) {
    return zonedDateTime.weekday === DayOfWeek.SATURDAY;
}

/**
 * Is given date Sunday?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Sunday
 * @type {boolean}
 */
function isSunday(zonedDateTime) {
    return zonedDateTime.weekday === DayOfWeek.SUNDAY;
}

/**
 * Is today Thanksgiving?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Thanksgiving
 * @type {boolean}
 */
function isThanksgivingDay(zonedDateTime) {
    // Fourth Thursday in November
    return zonedDateTime.month === Month.NOVEMBER
        && zonedDateTime.day >= 22
        && zonedDateTime.day <= 28
        && isThursday(zonedDateTime);
}

/**
 * Is given date Thursday?
 * @param {luxon.DateTime} zonedDateTime The date in question, often today
 * @return {boolean} true when today is Friday
 * @type {boolean}
 */
function isThursday(zonedDateTime) {
    return zonedDateTime.weekday === DayOfWeek.THURSDAY;
}

const to24Hour = (timeRaw) => {
    if(!isNaN(timeRaw.substr(timeRaw.length -1))){
        if(parseInt(timeRaw) < 12){
            timeRaw += 'A';
        }
        else{
            timeRaw += 'P';
        }
    }

    let amPm = timeRaw.substr(timeRaw.length -1);
    if(amPm.toLowerCase() === 'm'){
        amPm = timeRaw.substr(timeRaw.length - 2, 1);
    }

    let time;
    if(timeRaw.includes(':')) {
        time = parseInt(timeRaw.split(':')[0]);
    }
    else {
        time = parseInt(timeRaw.substr(0, timeRaw.length - 1));
    }

    if(
        amPm.toLowerCase() === 'p'
        && time < 12
    ){
        time = time + 12;
    }

    return time + ":00"
};

const toDenverTimeZone = (dateObject) => {
    if(dateObject instanceof luxon.DateTime){
        return dateObject.setZone(TIMEZONE_DENVER);
    }
    else {
        return luxon.DateTime.fromJSDate(dateObject, {zone: TIMEZONE_DENVER});

    }
}

/*** Takes startDate, expiryDate, givenDate - 
 * Checks if given/Provided Date falls 
 * between startDate and expiryDate */
const givenDateWithinDateRange = (startDate, expiryDate, givenDate) => {
    try {
    givenDate.setHours(0,0,0,0);
    startDate = new Date(startDate);
    startDate.setHours(0,0,0,0);
    expiryDate = new Date(expiryDate);
    expiryDate.setHours(0,0,0,0);
    if(givenDate < expiryDate && givenDate >= startDate){
        return true;
      }  
      return false;
    } catch (err) {
      ontech.log.error('Error at givenDateWithinDateRange', err);
    }
}

/** Check if a given date is today's -
 *  accepted Date String Format is yyyy-mm-dd */
const isDateToday = (dateStr) => {
    return dateStr === getDateString(new Date(), true, true);
  }
  
//** This takes any dateString and returns the weekDay name as Monday, Tuesday... */
  const getDayName = (dateString) => {
    let date = luxon.DateTime.fromISO(dateString, { zone: TIMEZONE_DENVER });
    return date.weekdayLong.toString();
  };

//** This takes any dateString and returns the Long Month name as in December, January... */
  const getMonthName = (dateString) => {
    let date = luxon.DateTime.fromISO(dateString, { zone: TIMEZONE_DENVER });
    return date.monthLong.toString();
  };